<template>
  <v-row dense>
    <v-col cols="12" :lg="stacked ? '6' : '4'">
      <v-select
        v-model="selectedBusinessEntity"
        :label="$t('templateEntity')"
        :items="businessEntities"
        :disabled="businessEntities.length < 2 || disabled"
        :no-data-text="$t('noBusinessEntitiesWithActivityTemplates')"
        color="black"
        item-color="primary"
        item-text="businessEntityName"
        return-object
        data-testid="template-entity"
      />
    </v-col>
    <v-col cols="12" :lg="stacked ? '6' : '3'">
      <v-select
        v-model="accountingCategory"
        :label="$t('accountingCategory')"
        :items="filteredCategories"
        color="black"
        item-color="primary"
        item-text="name"
        return-object
        data-testid="template-accounting-category"
        :disabled="disabled || !accountingCategoryMutable"
      />
    </v-col>
    <v-col cols="12" :lg="stacked ? '12' : '5'">
      <v-autocomplete
      v-model="activityTemplate"
      :label="$t('activityTemplates')"
      :filter="templateFilter"
      :items="filteredTemplates"
      ref="templateField"
      color="black"
      return-object
      item-value="activityTemplateId"
      :disabled="disabled"
      >
        <template #item="{item}">
          <v-container>
            <v-row dense>
              <v-col cols="auto" v-if="accountingCategory === $t('accountsPayable')">
                <Icon
                dataTestId="template-cost-type"
                icon-color="info"
                :icon="costIcons[item.costType]"
                :tooltipText="costTypeFromTemplate(item)"
                />
              </v-col>
              <v-col>
                <Icon
                v-if="item.specialization === TemplateSpecialization.Penalty.value"
                icon="weight-alert"
                :tooltipText="$t('penaltyActivity')"
                margin=""/>
                <Icon
                v-if="item.specialization === TemplateSpecialization.Depletion.value"
                icon="mdi-wallet-bifold"
                :tooltipText="$t('depletionActivity')"
                iconColor="black"
                margin=""/>
                <Icon
                v-if="item.specialization === TemplateSpecialization.Transfer.value"
                icon="mdi-transfer"
                :tooltipText="$t('transferActivity')"
                margin=""
                />
                <Icon
                v-if="showMissingMilesIcon(item)"
                icon="mdi-alert"
                icon-color="warning"
                :tooltipText="$t('contractMissingMilesToDestination')"
                margin=""
                />
                {{item.name}}
              </v-col>
              <v-col>
                <span>{{item.glCode}} {{item.glOffset ? `(${item.glOffset})` : ''}}</span>
              </v-col>
            </v-row>
          </v-container>
        </template>
        <template #selection="{item}">
          <Icon
          dataTestId="template-selection-cost-type"
          icon-color="info"
          :icon="costIcons[item.costType]"
          :tooltipText="costTypeFromTemplate(item)"
          />
          <Icon
          v-if="item.specialization === TemplateSpecialization.Penalty.value"
          icon="weight-alert"
          :tooltipText="$t('penaltyActivity')"/>
          <Icon
          v-if="item.specialization === TemplateSpecialization.Depletion.value"
          icon="mdi-wallet-bifold"
          :tooltipText="$t('depletionActivity')"
          iconColor="black"/>
          <Icon
          v-if="item.specialization === TemplateSpecialization.Transfer.value"
          icon="mdi-transfer"
          :tooltipText="$t('transferActivity')"
          />
          <Icon
          v-if="showMissingMilesIcon(item)"
          icon="mdi-alert"
          icon-color="warning"
          :tooltipText="$t('contractMissingMilesToDestination')"
          />
          {{item.name}}
        </template>
      </v-autocomplete>
    </v-col>
  </v-row>
</template>

<script>
import { TemplateAccountingCategory, ActivityStatus, CostType, ProductionCostType, PayOn, TemplateSpecialization, ActivityModifier } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { mapActions } from 'vuex'
export default {
  name: 'TemplateAutocomplete',

  props: {
    propAccountingCategories: Array,
    dataTextIds: Array,
    templateId: Number,
    focus: Boolean,
    includeModifiers: {
      type: Boolean,
      default: false
    },
    includeTransferActivities: {
      type: Boolean,
      default: false
    },
    includeReceivables: {
      type: Boolean,
      default: true
    },
    includeDefectActivities: {
      type: Boolean,
      default: true
    },
    includeDepletionActivities: {
      type: Boolean,
      default: true
    },
    includePenaltyActivities: {
      type: Boolean,
      default: true
    },
    stacked: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    distance: {
      type: Number,
      required: false,
      default: 0
    },
    accountingCategoryMutable: {
      type: Boolean,
      default: true
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    TemplateSpecialization,
    businessEntities: [],
    activityTemplates: [],
    costIcons: ['mdi-alpha-u-circle-outline', 'mdi-alpha-p-circle-outline', 'mdi-alpha-s-circle-outline', 'mdi-alpha-n-circle-outline'],
    accountingCategory: TemplateAccountingCategory.AccountsPayable,
    selectedBusinessEntity: undefined,
    activityTemplate: undefined
  }),

  created () {
    this.getInitialData()
  },

  computed: {
    accountingCategories () {
      return (!this.includeReceivables)
        ? TemplateAccountingCategory.names.filter((_, index) => index !== 1)
        : TemplateAccountingCategory.names
    },
    filteredTemplates () {
      if (!this.selectedBusinessEntity || this.filteredCategories.length === 0) {
        return []
      }

      const meetsCategoryRequirement = (template) => template.category === this.accountingCategory?.value
      const meetsTransferRequirement = (template) => template.specialization !== TemplateSpecialization.Transfer.value || this.includeTransferActivities
      const meetsDepletionRequirement = (template) => template.specialization !== TemplateSpecialization.Depletion.value || this.includeDepletionActivities
      const meetsActiveRequirement = (template) => template.activityStatus === ActivityStatus.Active.value || template.activityTemplateId === this.templateId
      const meetsDefectRequirement = (template) => (template.baseCost?.payOn !== PayOn.Defect.value && template.baseCost?.payOn !== PayOn.NatureDefect.value && template.baseCost?.payOn !== PayOn.MismanufacturedDefect.value) || this.includeDefectActivities
      const meetsPenaltyRequirement = (template) => (template.specialization !== TemplateSpecialization.Penalty.value || this.includePenaltyActivities)

      return this.selectedBusinessEntity.templates
        .filter(t => meetsCategoryRequirement(t) && meetsTransferRequirement(t) && meetsActiveRequirement(t) && meetsDefectRequirement(t) && meetsDepletionRequirement(t) && meetsPenaltyRequirement(t))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    filteredCategories () {
      if (!this.selectedBusinessEntity) {
        return []
      }

      if (!this.propAccountingCategories) {
        return [...this.selectedBusinessEntity.categories].sort((a, b) => (a.value - b.value))
      }

      return this.selectedBusinessEntity.categories
        .filter((category) => this.propAccountingCategories.includes(category.value))
        .sort((a, b) => (a.value - b.value))
    }
  },

  watch: {
    accountingCategory (val) {
      if (!this.activityTemplate) {
        return
      }

      this.activityTemplate = (val.value === this.activityTemplate.category)
        ? this.activityTemplate
        : undefined
    },

    selectedBusinessEntity (_, oldVal) {
      if (oldVal) this.activityTemplate = undefined

      if (this.filteredCategories.some(cat => cat.value === this.accountingCategory?.value)) {
        return
      }

      this.accountingCategory = this.activityTemplate
        ? TemplateAccountingCategory.forInt(this.activityTemplate.category)
        : this.filteredCategories.length > 0
          ? this.filteredCategories[0]
          : undefined
    },

    focus (val) {
      if (val) { this.$refs.templateField.focus() }
    },

    activityTemplate (val) {
      this.$emit('template-chosen', this.activityTemplate)
    },

    async propAccountingCategories (val) {
      if (!!this.activityTemplate && !val.includes(this.activityTemplate?.category)) {
        this.activityTemplate = undefined
        this.activityTemplates = await this.fetchActivityTemplates()
      }
      if (val.some(ac => ac !== this.accountingCategory?.value)) {
        this.accountingCategory = TemplateAccountingCategory.forInt(this.propAccountingCategories[0])
      }
    },

    templateId (v) {
      if (v === undefined || v === null) return
      this.activityTemplate = this.selectedBusinessEntity?.templates?.find(t => t.activityTemplateId === v)
    }
  },

  beforeDestroy () {
    if (this.selectedBusinessEntity) {
      localStorage.setItem(LocalStorageKeys.TEMPLATE_FORM_BUSINESS_ENTITY, this.selectedBusinessEntity.businessEntityId)
    }
  },

  methods: {
    ...mapActions('activity-templates', ['fetchActivityTemplates']),
    costTypeFromTemplate (template) {
      const costType = CostType.forInt(template.costType)
      if (costType !== CostType.Production) return costType.name

      const productionCostType = ProductionCostType.forInt(template.productionType)
      return productionCostType === ProductionCostType.Other
        ? costType.name
        : `${costType.name} - ${productionCostType.name}`
    },
    async getInitialData () {
      const activityTemplates = await this.fetchActivityTemplates()
      const businessEntities = this.getBusinessEntities(activityTemplates)
      this.businessEntities = businessEntities
      this.setInitialItems(businessEntities)
    },

    setInitialItems (businessEntities) {
      if (this.templateId) {
        for (let i = 0; i < businessEntities.length; i++) {
          const entity = businessEntities[i]
          const activityTemplate = entity.templates.find(t => t.activityTemplateId === this.templateId)

          if (activityTemplate) {
            this.selectedBusinessEntity = entity
            this.accountingCategory = TemplateAccountingCategory.forInt(activityTemplate.category)
            this.activityTemplate = activityTemplate
            break
          }
        }

        return
      }

      const businessEntityFromCookie = businessEntities.find(be => localStorage.getItem(LocalStorageKeys.TEMPLATE_FORM_BUSINESS_ENTITY) === String(be.businessEntityId))

      this.selectedBusinessEntity = businessEntityFromCookie || businessEntities[0]
      this.accountingCategory = this.filteredCategories[0]
      if (this.selectedBusinessEntity.templates.filter(template => template.category === this.accountingCategory?.value &&
          template.activityStatus === ActivityStatus.Active.value).length === 1) {
        this.activityTemplate = this.selectedBusinessEntity.templates[0]
      }
    },

    getBusinessEntities (templates) {
      const filteredTemplates = templates.filter(template => this.includeModifiers || template.modifier !== ActivityModifier.DistanceTraveled.value)
      const groupedTemplates = Object.groupBy(filteredTemplates, template => template.businessEntityId)

      return Object.values(groupedTemplates).map(templates => {
        const firstTemplate = templates[0]
        const entity = {
          businessEntityId: firstTemplate.businessEntityId,
          businessEntityName: firstTemplate.businessEntityName,
          businessEntityExportCode: firstTemplate.businessEntityExportCode,
          templates: templates
        }

        const entityCategories = entity.templates
          .map(template => template.category)
          .filter(category => this.includeReceivables || category !== TemplateAccountingCategory.AccountsReceivable.value)

        return {
          ...entity,
          categories: [...new Set(entityCategories)].map(category => TemplateAccountingCategory.forInt(category))
        }
      })
    },

    templateFilter (item, queryText) {
      const { glCode, glOffset, name } = item
      return glCode.toLowerCase().indexOf(queryText.toLowerCase()) > -1 ||
        glOffset.toLowerCase().indexOf(queryText.toLowerCase()) > -1 ||
        name.toLowerCase().indexOf(queryText.toLowerCase()) > -1
    },

    showMissingMilesIcon (item) {
      return item.modifier === ActivityModifier.DistanceTraveled.value &&
             (this.distance === 0 || this.distance === null)
    }
  }
}
</script>
